/**
 * Date utility functions for daily quote selection
 */

/**
 * Get today's date as an ISO string (YYYY-MM-DD)
 * Uses local timezone, not UTC
 * @returns ISO date string, e.g., "2026-02-09"
 */
export function getTodayDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if two date strings represent the same day
 * @param date1 - First date string (YYYY-MM-DD)
 * @param date2 - Second date string (YYYY-MM-DD)
 * @returns True if dates are the same
 */
export function isSameDay(date1: string | null, date2: string | null): boolean {
  return date1 === date2;
}

/**
 * Get a date string for a specific Date object
 * @param date - Date object
 * @returns ISO date string (YYYY-MM-DD)
 */
export function getDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if we've crossed into a new day
 * Useful for detecting when the daily quote should change
 * @param lastDate - Last stored date string
 * @returns True if we're on a different day than lastDate
 */
export function hasDateChanged(lastDate: string | null): boolean {
  if (!lastDate) return true;
  const today = getTodayDateString();
  return !isSameDay(lastDate, today);
}

/**
 * Format a timestamp to a readable date string
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted date string, e.g., "Feb 9, 2026"
 */
export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a timestamp to a relative time string
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Relative time string, e.g., "2 hours ago"
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'just now';
}
